import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlTableState from '../../../hooks/useUrlTableState';
import {
  Edit,
  Clock,
  Calendar,
  Search,
  Plus,
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  User,
  Send,
  Eye,
  Settings,
  X,
  Download,
  Mail,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { exportToCSV, generateExportFilename, downloadFromBackend } from "../../../utils/exportUtils";
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { useNotification } from "../../../components/common/NotificationProvider";
import { formatDate } from "../../../utils/stringUtils";
import { regularizationReasonService } from "../../../api/crm/config/configService";
import WarningPopup from "../../../components/WarningPopup";
import Modal from "../../../components/common/Modal";
import RegularizationRequestModal from "../../../components/hrms/RegularizationRequestModal";
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from "../../../utils/formValidation";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";

// Helper function to format time strings (HH:MM) or Date objects to 24-hour format
// This extracts time the same way as the employee view to ensure consistency
const formatTime = (timeValue) => {
  if (!timeValue) return "Not recorded";

  try {
    let hours, minutes;

    // If it's already a time string (HH:MM or HH:MM:SS), extract hours and minutes
    if (typeof timeValue === 'string' && timeValue.match(/^\d{2}:\d{2}/)) {
      [hours, minutes] = timeValue.split(':').map(Number);
    }
    // If it's a Date object or ISO string, extract time using toTimeString() method
    // This matches how the backend formats times and ensures consistency with employee view
    else {
      const date = new Date(timeValue);
      if (isNaN(date.getTime())) {
        return "Not recorded";
      }
      // Use toTimeString() to get consistent time extraction (same as employee view)
      const timeString = date.toTimeString().split(' ')[0]; // Gets "HH:MM:SS"
      [hours, minutes] = timeString.split(':').map(Number);
    }

    // Format as 12-hour AM/PM format
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const formattedMinutes = String(minutes).padStart(2, '0');
    return `${displayHours}:${formattedMinutes} ${ampm}`;
  } catch (error) {
    return "Not recorded";
  }
};

const AttendanceRegularization = ({ onBack, userRole = "hr_manager", initialMode = "list", id, onClose }) => {
  const { showSuccess, showError, showWarning, showInfo } = useNotification();

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    status: 'all',
    startDate: '',
    endDate: ''
  }), []);

  const {
    filters: regularizationFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateRegularizationFilter,
    updatePage: updateRegularizationPage,
    updateLimit: updateRegularizationLimit,
    resetFilters: resetRegularizationFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const filterStatus = regularizationFilters.status || 'all';
  const setFilterStatus = useCallback((status) => {
    updateRegularizationFilter({ status });
  }, [updateRegularizationFilter]);

  const filterStartDate = regularizationFilters.startDate || '';
  const filterEndDate = regularizationFilters.endDate || '';

  const setDateRange = useCallback((range) => {
    updateRegularizationFilter({ startDate: range.startDate, endDate: range.endDate });
  }, [updateRegularizationFilter]);

  const setFilterStartDate = useCallback((startDate) => {
    updateRegularizationFilter({ startDate });
  }, [updateRegularizationFilter]);

  const setFilterEndDate = useCallback((endDate) => {
    updateRegularizationFilter({ endDate });
  }, [updateRegularizationFilter]);

  const currentPage = regularizationFilters.page;
  const setCurrentPage = useCallback((page) => {
    updateRegularizationPage(page);
  }, [updateRegularizationPage]);

  const itemsPerPage = regularizationFilters.limit;
  const setItemsPerPage = useCallback((limit) => {
    updateRegularizationLimit(limit);
  }, [updateRegularizationLimit]);

  const [regularizationRequests, setRegularizationRequests] = useState([]);
  const [searchMetadata, setSearchMetadata] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // Pagination state
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  const [showRequestForm, setShowRequestForm] = useState(initialMode === "create");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(initialMode === "view");
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: "",
    checkIn: "",
    checkOut: "",
    reason: "",
    reasonId: "",
    description: "",
    employeeId: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // State for searchable employee dropdown
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState("");

  // Store selected employee data to persist even after search is cleared
  const [selectedEmployeeData, setSelectedEmployeeData] = useState(null);

  // Export and Selection State
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // Confirmation state for Approve/Reject
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    requestId: null,
    status: null,
    employeeName: null,
    comments: ""
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);

  // Get query client for invalidating queries
  const queryClient = useQueryClient();

  // Fetch regularization reasons for the form
  const { data: reasonsData } = useQuery({
    queryKey: ["regularizationReasons", "dropdown"],
    queryFn: () => regularizationReasonService.getDropdown()
  });
  const dynamicReasons = reasonsData?.data || [];
  const hardcodedReasons = [];
  // const displayReasons = dynamicReasons.length > 0 ? dynamicReasons.map(r => r.name) : hardcodedReasons;

  useEffect(() => {
    loadRegularizationRequests();
  }, []);

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setPageSize(limit);
  };

  // Reset selection when filters change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [searchTerm, filterStatus, itemsPerPage, filterStartDate, filterEndDate]);

  // Load data when pagination or filters change
  useEffect(() => {
    loadRegularizationRequests();
  }, [searchTerm, filterStatus, currentPage, itemsPerPage, filterStartDate, filterEndDate]);

  // Debounce employee search for dropdown
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearchTerm);
    }, 300); // 300ms debounce delay for employee search

    return () => clearTimeout(timer);
  }, [employeeSearchTerm]);

  // Handle deep-linking to a specific request
  useEffect(() => {
    if (id && initialMode === "view") {
      const fetchRequestById = async () => {
        setIsLoading(true);
        try {
          const token = localStorage.getItem("auth_token");
          const url = `${process.env.REACT_APP_API_BASE_URL}/regularization-test/${id}`;
          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const result = await response.json();
          if (result.success && result.data) {
            setSelectedRequest(result.data);
            setShowDetails(true);
          } else {
            showError("Failed to load request details");
          }
        } catch (error) {
          showError("An error occurred while loading request details");
        } finally {
          setIsLoading(false);
        }
      };
      fetchRequestById();
    }
  }, [id, initialMode]);

  // Reset selection when page changes
  useEffect(() => {
    setSelectedRows(new Set());
  }, [currentPage]);

  // Auto-fetch times when employee and date are selected
  useEffect(() => {
    if (formData.employeeId && formData.date && showRequestForm) {
      const fetchAttendanceTimes = async () => {
        try {
          const token = localStorage.getItem("auth_token");
          const url = `${process.env.REACT_APP_API_BASE_URL}/regularization-test/attendance-times/fetch?employeeId=${formData.employeeId}&date=${formData.date}`;
          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const result = await response.json();
          if (result.success && result.data) {
             const { checkIn, checkOut } = result.data;
             setFormData(prev => ({
               ...prev,
               checkIn: checkIn || "",
               checkOut: checkOut || ""
             }));
          }
        } catch (error) {
          console.error("Error fetching attendance times:", error);
        }
      };
      
      fetchAttendanceTimes();
    }
  }, [formData.employeeId, formData.date, showRequestForm]);

  // React Query: Search Employees (fetch when dropdown is open, load initial if search empty)
  const { data: searchedEmployeesData, isLoading: isLoadingEmployeeSearch } = useQuery({
    queryKey: ['regularization-employees-search', debouncedEmployeeSearch],
    queryFn: async () => {
      const trimmedSearch = debouncedEmployeeSearch?.trim() || "";
      const url = `${process.env.REACT_APP_API_BASE_URL}/dropdown-data/employees?search=${encodeURIComponent(trimmedSearch)}&limit=50&includeSuperAdmin=true`;

      const token = localStorage.getItem("auth_token");
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          return data.data.employees || [];
        }
      }
      return [];
    },
    enabled: employeeSearchOpen, // Call when search dropdown is open
    staleTime: 0,
  });

  // React Query: Delete Regularization Request Mutation
  const deleteRequestMutation = useMutation({
    mutationFn: async (requestId) => {
      const token = localStorage.getItem("auth_token");
      const url = `${process.env.REACT_APP_API_BASE_URL}/regularization-test/${requestId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete regularization request');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["regularization-requests"] });
      queryClient.invalidateQueries({ queryKey: ["regularization-stats"] }); // If stats exist
    },
  });

  // Get employees to display in dropdown (only searched results, no initial load)
  const dropdownEmployees = searchedEmployeesData || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (employeeSearchOpen && !event.target.closest('.employee-dropdown-container')) {
        setEmployeeSearchOpen(false);
        setEmployeeSearchTerm("");
      }
    };

    if (employeeSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [employeeSearchOpen]);

  // Close dropdown when form closes (but preserve selectedEmployeeData)
  useEffect(() => {
    if (!showRequestForm) {
      setEmployeeSearchOpen(false);
      setEmployeeSearchTerm("");
      // Don't clear selectedEmployeeData here - it should persist until form is reset
    }
  }, [showRequestForm]);

  const loadRegularizationRequests = async () => {
    setIsLoading(true);
    setIsSearching(true);
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }

      if (filterStatus !== 'all') {
        queryParams.append('status', filterStatus);
      }

      if (filterStartDate) {
        queryParams.append('startDate', filterStartDate);
      }

      if (filterEndDate) {
        queryParams.append('endDate', filterEndDate);
      }

      // Add pagination
      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', itemsPerPage.toString());

      const url = `${process.env.REACT_APP_API_BASE_URL}/regularization-test?${queryParams.toString()}`;

      const token = localStorage.getItem("auth_token");
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success) {
        setRegularizationRequests(data.data.requests || []);
        setSearchMetadata(data.data.searchMetadata || null);

        // Update pagination state from API response
        if (data.data.pagination) {
          setPagination({
            currentPage: data.data.pagination.currentPage || currentPage,
            totalPages: data.data.pagination.totalPages || 1,
            totalItems: data.data.pagination.totalItems || 0,
            itemsPerPage: data.data.pagination.itemsPerPage || itemsPerPage,
            hasNextPage: data.data.pagination.hasNextPage ?? false,
            hasPrevPage: data.data.pagination.hasPrevPage ?? false,
            limit: data.data.pagination.limit || itemsPerPage
          });
        }
      } else {
        showError(`Failed to load regularization requests: ${data.message}`);
        setRegularizationRequests([]);
        setSearchMetadata(null);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: itemsPerPage,
          hasNextPage: false,
          hasPrevPage: false,
          limit: itemsPerPage
        });
      }
    } catch (error) {
      showError("Failed to load regularization requests. Please try again.");
      setRegularizationRequests([]);
      setSearchMetadata(null);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: itemsPerPage,
        hasNextPage: false,
        hasPrevPage: false,
        limit: itemsPerPage
      });
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };



  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "pending":
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getToggleIcon = (status) => {
    switch (status) {
      case "pending":
        return <CheckCircle className="h-5 w-5" />;
      case "approved":
        return <XCircle className="h-5 w-5" />;
      case "rejected":
        return <CheckCircle className="h-5 w-5" />;
      default:
        return <Settings className="h-5 w-5" />;
    }
  };

  const getToggleText = (status) => {
    switch (status) {
      case "pending":
        return "Approve";
      case "approved":
        return "Reject";
      case "rejected":
        return "Approve";
      default:
        return "Toggle";
    }
  };

  const getToggleTooltip = (status) => {
    switch (status) {
      case "pending":
        return "Click to Approve";
      case "approved":
        return "Click to Reject";
      case "rejected":
        return "Click to Approve";
      default:
        return "Toggle Status";
    }
  };

  const getToggleColor = (status) => {
    switch (status) {
      case "pending":
        return "text-green-600 hover:text-green-800 hover:bg-green-100";
      case "approved":
        return "text-red-600 hover:text-red-800 hover:bg-red-100";
      case "rejected":
        return "text-green-600 hover:text-green-800 hover:bg-green-100";
      default:
        return "text-gray-600 hover:text-gray-800 hover:bg-blue-100/60";
    }
  };

  const triggerStatusChange = (request, status) => {
    setConfirmModal({
      isOpen: true,
      requestId: request._id || request.id,
      status: status,
      employeeName: request.employeeName || (request.employeeId?.personalInfo ? `${request.employeeId.personalInfo.firstName} ${request.employeeId.personalInfo.lastName}` : "Employee"),
      comments: ""
    });
  };

  const handleConfirmAction = async () => {
    if (confirmModal.requestId) {
      await handleStatusChange(confirmModal.requestId, confirmModal.status, confirmModal.comments);
      setConfirmModal({ ...confirmModal, isOpen: false });
    }
  };

  const handleStatusChange = async (requestId, newStatus, comments = "") => {
    try {
      let response;
      if (newStatus === "approved") {
        const token = localStorage.getItem("auth_token");
        response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/regularization-test/${requestId}/approve`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              comments: comments || "Approved by HR Manager",
            }),
          }
        );
      } else if (newStatus === "rejected") {
        const token = localStorage.getItem("auth_token");
        response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/regularization-test/${requestId}/reject`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              rejectionReason: comments || "Rejected by HR Manager",
              comments: comments || "Rejected by HR Manager",
            }),
          }
        );
      } else if (newStatus === "pending") {
        const token = localStorage.getItem("auth_token");
        response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/regularization-test/${requestId}/pending`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              comments: comments || "Set to pending by HR Manager",
            }),
          }
        );
      }

      const data = await response.json();

      if (data.success) {
        showSuccess(`Request ${newStatus} successfully!`);
        await loadRegularizationRequests();
      } else {
        // Handle specific error messages with better UX
        const errorMessage = data.message || data.error || "";
        const errorText = errorMessage.toLowerCase();

        if (errorText.includes("already approved")) {
          showWarning("This request is already approved. No action needed.");
        } else if (errorText.includes("already rejected")) {
          showWarning("This request is already rejected. No action needed.");
        } else if (errorText.includes("already pending")) {
          showWarning("This request is already pending. No action needed.");
        } else {
          showError(errorMessage || `Unable to ${newStatus} request. Please try again.`);
        }
      }
    } catch (error) {
      showError(`Failed to ${newStatus} request. Please try again.`);
    }
  };

  const handleToggleApproval = async (request) => {
    const requestId = request._id || request.id;
    const currentStatus = request.status;

    if (currentStatus === "pending") {
      // If pending, approve it
      await handleStatusChange(requestId, "approved");
    } else if (currentStatus === "approved") {
      // If approved, reject it
      await handleStatusChange(requestId, "rejected");
    } else if (currentStatus === "rejected") {
      // If rejected, approve it
      await handleStatusChange(requestId, "approved");
    }
  };

  const validateDescription = (description) => {
    if (!description || description.trim().length < 10) {
      return "Description must be at least 10 characters";
    }
    if (description.length > 500) {
      return "Description must be no more than 500 characters";
    }
    if (!VALID_TEXT_REGEX.test(description)) {
      return "Description contains invalid characters";
    }
    return null;
  };

  // Handle key press for text inputs - allow approved symbols
  const handleTextKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };

  const handleFieldChange = (name, value) => {
    if (name === 'reason') {
      const reasonObj = dynamicReasons.find(r => (r._id || r.id) === value);
      if (reasonObj) {
        setFormData(prev => ({ ...prev, reason: reasonObj.name, reasonId: value }));
      } else {
        setFormData(prev => ({ ...prev, reason: value, reasonId: "" }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Field-level validation
    let error = "";
    if (name === 'employeeId' && !value) error = "Employee selection is required";
    if (name === 'date') {
      if (!value) {
        error = "Date is required";
      } else {
        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        selectedDate.setHours(0, 0, 0, 0);
        const diffDays = Math.round((today - selectedDate) / (1000 * 60 * 60 * 24));
        
        if (diffDays > 3) {
          error = "Regularization can only be requested within 3 days of the attendance date";
        } else if (diffDays < 0) {
          error = "Cannot select a future date";
        }
      }
    }
    if (name === 'reason' && !value) error = "Reason is required";
    if (name === 'checkIn' && !value) error = "Check-in time is required";
    if (name === 'checkOut' && !value) error = "Check-out time is required";

    if (name === 'description') {
      error = validateDescription(value);
    }

    // Time cross-validation
    if (name === 'checkIn' || name === 'checkOut') {
      const otherField = name === 'checkIn' ? 'checkOut' : 'checkIn';
      const otherValue = formData[otherField];
      if (value && otherValue) {
        const checkInVal = name === 'checkIn' ? value : otherValue;
        const checkOutVal = name === 'checkIn' ? otherValue : value;
        if (checkOutVal <= checkInVal) {
          setFormErrors(prev => ({ ...prev, timeValidation: "Check-out must be after check-in", [name]: "" }));
        } else {
          setFormErrors(prev => ({ ...prev, timeValidation: "", [name]: "" }));
        }
      }
    }

    setFormErrors(prev => ({ ...prev, [name]: error }));
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Get sorted data
  const getSortedRequests = (requests) => {
    if (!sortField || !requests || requests.length === 0) {
      return requests;
    }

    const sorted = [...requests].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'employee':
          aValue = (a.employeeName ||
            (a.employeeId?.personalInfo
              ? `${a.employeeId.personalInfo.firstName || ""} ${a.employeeId.personalInfo.lastName || ""}`.trim()
              : "Unknown Employee")).toLowerCase();
          bValue = (b.employeeName ||
            (b.employeeId?.personalInfo
              ? `${b.employeeId.personalInfo.firstName || ""} ${b.employeeId.personalInfo.lastName || ""}`.trim()
              : "Unknown Employee")).toLowerCase();
          break;

        case 'regularizationDate':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;

        case 'requestDate':
          aValue = new Date(a.submittedDate || a.createdAt).getTime();
          bValue = new Date(b.submittedDate || b.createdAt).getTime();
          break;

        case 'requestedTime':
          // Sort by check-in time
          aValue = a.requestedCheckIn || "";
          bValue = b.requestedCheckIn || "";
          break;

        case 'reason':
          aValue = (a.reason || "").toLowerCase();
          bValue = (b.reason || "").toLowerCase();
          break;

        case 'status':
          aValue = (a.status || "").toLowerCase();
          bValue = (b.status || "").toLowerCase();
          break;

        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Compare values
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    if (name === 'description') {
      processedValue = value.replace(INVALID_CHARS_REGEX, '');
      if (processedValue.length > 500) processedValue = processedValue.slice(0, 500);
    }

    handleFieldChange(name, processedValue);
  };

  // --- Export and Selection Handlers ---

  const handleSelectAll = (e) => {
    const currentPageIds = regularizationRequests.map(req => req._id || req.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const handleDeleteRequest = (request) => {
    setRequestToDelete(request);
    setIsBulkDelete(false);
    setShowDeleteModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteModal(true);
  };

  const confirmDeleteRequest = async () => {
    if (!requestToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;
        let errorMessages = [];

        for (const id of ids) {
          try {
            await deleteRequestMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
            errorMessages.push(error.message);
          }
        }

        if (successCount > 0) {
          showSuccess(`${successCount} request(s) deleted successfully.`);
          setSelectedRows(new Set());
        }
        if (failCount > 0) {
          // Show specific error message if available, otherwise generic message
          const errorMessage = errorMessages.length > 0
            ? errorMessages[0]
            : `${failCount} request(s) could not be deleted.`;
          showError(errorMessage);
        }
      } else {
        await deleteRequestMutation.mutateAsync(requestToDelete._id || requestToDelete.id);
        showSuccess('Regularization request deleted successfully.');
      }
      setShowDeleteModal(false);
      setRequestToDelete(null);
      setIsBulkDelete(false);
      await loadRegularizationRequests();
    } catch (error) {
      if (!isBulkDelete) {
        // Show specific error message from API if available
        const errorMessage = error.message || 'Failed to delete regularization request. Please try again.';
        showError(errorMessage);
      }
    }
  };

  const mapRequestsForExport = (requests) => {
    return requests.map(req => {
      const empName = req.employeeName || (req.employeeId?.personalInfo?.firstName ? `${req.employeeId.personalInfo.firstName} ${req.employeeId.personalInfo.lastName}` : 'Unknown');
      const empId = req.employeeId?.employeeId || '';
      const date = req.date ? new Date(req.date).toISOString().split('T')[0].split('-').reverse().join('/') : '';

      // Helper to safely get time string
      const getSafeTime = (val) => {
        if (!val) return '';
        if (typeof val === 'string') return val.substring(0, 5);
        if (val.toTimeString) return val.toTimeString().split(' ')[0].substring(0, 5);
        return '';
      };

      const reqIn = getSafeTime(req.requestedCheckIn);
      const reqOut = getSafeTime(req.requestedCheckOut);
      const origIn = getSafeTime(req.originalCheckIn);
      const origOut = getSafeTime(req.originalCheckOut);

      return {
        'Employee Name': empName,
        'Employee ID': empId,
        'Date': date,
        'Requested Check-In': reqIn,
        'Requested Check-Out': reqOut,
        'Reason': req.reason || '',
        'Status': (req.status || 'pending').charAt(0).toUpperCase() + (req.status || 'pending').slice(1),
        'Applied Date': req.submittedDate ? new Date(req.submittedDate).toLocaleDateString('en-IN') : ''
      };
    });
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Build base export filters for backend API
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: searchTerm || undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      startDate: filterStartDate || undefined,
      endDate: filterEndDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [searchTerm, filterStatus, filterStartDate, filterEndDate]);

  // Get current page request IDs
  const currentPageRequestIds = useMemo(
    () =>
      regularizationRequests
        .map((req) => {
          const raw = req?._id ?? req?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [regularizationRequests]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageRequestIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'regularization',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortField,
            sortOrder,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `regularization-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      sortField,
      sortOrder,
      currentPageRequestIds,
    ]
  );

  // Handle export for selected requests with format selection
  const handleSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        showWarning('No selected requests to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        showSuccess('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport, showSuccess, showWarning, showError]
  );

  // Handle scoped export from modal (all, filtered, currentPage, dateRange)
  const handleScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            showWarning('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageRequestIds,
          });
        } else {
          return;
        }

        showSuccess('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      regularizationRequests.length,
      baseExportFilters,
      requestBackendExport,
      itemsPerPage,
      currentPageRequestIds,
      showSuccess,
      showWarning,
      showError,
    ]
  );

  // Legacy handlers for backward compatibility (deprecated)
  const handleExportCurrentPage = () => {
    const exportData = mapRequestsForExport(regularizationRequests);
    exportToCSV(exportData, 'regularization_page', {
      title: 'Regularization Requests',
      type: 'Current Page'
    });
    setShowExportModal(false);
    showSuccess('Current page exported!');
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", `Date Range (${startDate} to ${endDate})`);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (searchTerm) queryParams.append("search", searchTerm);
      if (filterStatus !== 'all') queryParams.append("status", filterStatus);

      const url = `${process.env.REACT_APP_API_BASE_URL}/regularization-test/export?${queryParams.toString()}`;
      const filename = generateExportFilename('regularization_range');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      showSuccess('Export started successfully');
    } catch (error) {
      showError("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", "All Data");
      if (searchTerm) queryParams.append("search", searchTerm);
      if (filterStatus !== 'all') queryParams.append("status", filterStatus);

      const url = `${process.env.REACT_APP_API_BASE_URL}/regularization-test/export?${queryParams.toString()}`;
      const filename = generateExportFilename('all_regularization');
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      showSuccess('All requests export started');
    } catch (error) {
      showError("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();

    // Final validation before submission
    const errors = {};
    if (!formData.employeeId) errors.employeeId = "Employee selection is required";
    if (!formData.date) errors.date = "Date is required";
    if (!formData.reason) errors.reason = "Reason is required";
    if (!formData.checkIn) errors.checkIn = "Check-in time is required";
    if (!formData.checkOut) errors.checkOut = "Check-out time is required";

    const descriptionError = validateDescription(formData.description);
    if (descriptionError) errors.description = descriptionError;

    if (formData.checkIn && formData.checkOut && formData.checkOut <= formData.checkIn) {
      errors.timeValidation = "Check-out must be after check-in";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showError("Please fix the errors in the form before submitting.");
      return;
    }

    setFormErrors({});

    try {

      // Validate employee selection
      if (!formData.employeeId) {
        showError("❌ Please select an employee");
        return;
      }

      const requestData = {
        employeeId: formData.employeeId, // Use MongoDB _id from selectedEmployeeData
        date: formData.date,
        requestedCheckIn: formData.checkIn,
        requestedCheckOut: formData.checkOut,
        reason: formData.reason,
        reasonId: formData.reasonId || null,
        description: formData.description,
        requestType: "both",
        priority: "medium",
      };

      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/regularization-test`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(requestData),
        }
      );

      const data = await response.json();

      if (data.success) {
        showSuccess("Regularization request submitted successfully!");
        setFormData({
          date: "",
          checkIn: "",
          checkOut: "",
          reason: "",
          reasonId: "",
          description: "",
          employeeId: "",
        });
        setFormErrors({});
        setSelectedEmployeeData(null);
        setEmployeeSearchOpen(false);
        setEmployeeSearchTerm("");
        setShowRequestForm(false);
        if (onClose) onClose();
        // Reload requests to show the new one
        await loadRegularizationRequests();
      } else {
        // Handle specific validation errors
        const errorMessage = data.message || data.error || "";
        const errorText = errorMessage.toLowerCase();

        if (errorText.includes("already exists") || errorText.includes("regularization request already exists")) {
          showWarning("A regularization request already exists for this date. Please select a different date or check existing requests.");
        } else if (errorText.includes("workinghours")) {
          showError("Invalid time range: Check-out time must be after check-in time. Please correct the times.");
        } else if (errorText.includes("validation failed")) {
          showError("Validation failed: Please check all required fields and ensure times are valid.");
        } else {
          showError(errorMessage || "Unable to submit request. Please try again.");
        }
      }
    } catch (error) {
      showError("Failed to submit regularization request. Please try again.");
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetails(true);
  };

  const handleApproveRequest = async (requestId) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/regularization-test/${requestId}/approve`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            comments: "Approved by HR Manager",
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        showSuccess("Regularization request approved successfully!");
        await loadRegularizationRequests();
      } else {
        // Handle specific error messages with better UX
        const errorMessage = data.message || data.error || "";
        const errorText = errorMessage.toLowerCase();

        if (errorText.includes("already approved")) {
          showWarning("This request is already approved. No action needed.");
        } else {
          showError(errorMessage || "Unable to approve request. Please try again.");
        }
      }
    } catch (error) {
      showError("Failed to approve regularization request. Please try again.");
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/regularization-test/${requestId}/reject`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            rejectionReason: "Rejected by HR Manager",
            comments: "Rejected by HR Manager",
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        showSuccess("Regularization request rejected successfully!");
        await loadRegularizationRequests();
      } else {
        // Handle specific error messages with better UX
        const errorMessage = data.message || data.error || "";
        const errorText = errorMessage.toLowerCase();

        if (errorText.includes("already rejected")) {
          showWarning("This request is already rejected. No action needed.");
        } else if (errorText.includes("already approved")) {
          showWarning("This request is already approved. You may need to change its status first.");
        } else {
          showError(errorMessage || "Unable to reject request. Please try again.");
        }
      }
    } catch (error) {
      showError("Failed to reject regularization request. Please try again.");
    }
  };


  if (showDetails && selectedRequest) {
    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowDetails(false);
                if (onClose) onClose();
              }}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold text-gray-900">
              Regularization Request Details
            </h2>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Employee Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Employee Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-2">
                  <User className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-600">Name:</span>
                    <span className="text-sm font-medium break-words capitalize block">
                      {selectedRequest.employeeName ||
                        (selectedRequest.employeeId?.personalInfo
                          ? `${selectedRequest.employeeId.personalInfo.firstName ||
                            ""
                            } ${selectedRequest.employeeId.personalInfo.lastName ||
                            ""
                            }`.trim()
                          : "Unknown Employee")}
                    </span>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <FileText className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-600">Employee ID:</span>
                    <span className="text-sm font-medium break-words block">
                      {selectedRequest.employeeOfficialId || "No ID"}
                    </span>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <Mail className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-600">Email:</span>
                    <span className="text-sm font-medium break-words block">
                      {selectedRequest.officialEmail || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Request Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Request Information
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Date:</span>
                  <span className="text-sm font-medium">
                    {formatDate(selectedRequest.date, { includeTime: false })}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(selectedRequest.status)}
                  <span className="text-sm text-gray-600">Status:</span>
                  <span
                    className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(
                      selectedRequest.status
                    )}`}
                  >
                    {selectedRequest.status.charAt(0).toUpperCase() +
                      selectedRequest.status.slice(1)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600">Submitted:</span>
                  <span className="text-sm font-medium">
                    {formatDate(selectedRequest.createdAt || selectedRequest.submittedDate, { includeTime: true })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Time Details */}
          <div className="mt-6 bg-brand/5 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Requested Time Details
            </h3>
            <div className="space-y-1">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Check In:</span> {formatTime(selectedRequest.requestedCheckIn)}
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-medium">Check Out:</span> {formatTime(selectedRequest.requestedCheckOut)}
              </div>
            </div>
          </div>

          {/* Reason and Description */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Reason</h3>
            <p className="text-sm text-gray-600 mb-4">
              {selectedRequest.reason}
            </p>

            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Description
            </h3>
            <p className="text-sm text-gray-600 break-words whitespace-normal first-letter:capitalize">
              {selectedRequest.description}
            </p>
          </div>

          {/* Approval Information */}
          {selectedRequest.status !== "pending" && (
            <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Additional Information
              </h3>
              <div className="space-y-3">
                {selectedRequest.approvedDate && (
                  <div className="flex items-center space-x-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {selectedRequest.status === "approved" ? "Approved On:" : "Rejected On:"}
                    </span>
                    <span className="text-sm font-medium">
                      {formatDate(selectedRequest.approvedDate, { includeTime: true })}
                    </span>
                  </div>
                )}
                {(selectedRequest.approvedByName || selectedRequest.approvedBy) && (
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">{selectedRequest.status === "approved" ? "Approved By:" : "Action By:"}</span>
                    <span className="text-sm font-medium">
                      {selectedRequest.approvedByName ||
                        (selectedRequest.approvedBy?.firstName ? `${selectedRequest.approvedBy.firstName} ${selectedRequest.approvedBy.lastName}` : "Approver")}
                    </span>
                  </div>
                )}
                {selectedRequest.status === "rejected" && selectedRequest.rejectionReason && (
                  <div className="mt-2 text-sm">
                    <span className="text-gray-600 font-medium">Rejection Reason:</span>
                    <p className="mt-1 text-red-600 bg-red-50 p-2 rounded border border-red-100 whitespace-pre-wrap first-letter:capitalize">
                      {selectedRequest.rejectionReason}
                    </p>
                  </div>
                )}
                {selectedRequest.comments && selectedRequest.comments !== selectedRequest.rejectionReason && (
                  <div className="mt-2 text-sm">
                    <span className="text-gray-600 font-medium">Approver Comments:</span>
                    <p className="mt-1 text-gray-800 bg-white p-2 rounded border border-gray-200 whitespace-pre-wrap first-letter:capitalize">
                      {selectedRequest.comments}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }



  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Attendance Regularization
            </h2>
          </div>
          <div className="flex space-x-2">
            {selectedRows.size > 0 && (
              <PermissionGate permission="attendance" action="delete">
                <Button
                  variant="primary"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={handleBulkDelete}
                  className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
                >
                  Delete ({selectedRows.size})
                </Button>
              </PermissionGate>
            )}
            <div className="relative" ref={selectedExportWrapRef}>
              <Button
                variant="outline"
                size="sm"
                icon={
                  exportLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )
                }
                onClick={handleExportClick}
                disabled={exportLoading}
                className={`flex-shrink-0 mr-2 ${selectedRows.size > 0 ? 'bg-brand/5 border-brand/20 text-brand' : ''}`}
                aria-label={
                  selectedRows.size > 0
                    ? `Export ${selectedRows.size} selected requests`
                    : 'Export requests'
                }
              >
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
              </Button>
              <SelectedExportFormatBar
                show={selectedRows.size > 0 && showInlineSelectedFormats}
                panelEntered={inlineFormatPanelEntered}
                disabled={exportLoading}
                onSelect={handleSelectedFormatExport}
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowRequestForm(true)}
              className="flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>New Request</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by Employee Name, Employee ID or Email ID..."
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              maxLength={100}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  // Maintain focus on search input
                  const searchInput = document.querySelector('input[placeholder="Search requests..."]');
                  if (searchInput) {
                    setTimeout(() => searchInput.focus(), 0);
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Date Filter Range */}
          <div className="flex items-center gap-2">
            <DateRangeFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              onChange={setDateRange}
            />

            {/* Clear Filters */}
            {(searchTerm || filterStatus !== "all" || filterStartDate || filterEndDate) && (
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  resetRegularizationFilters();
                }}
                className="h-[44px] border-gray-200 text-gray-600 hover:bg-blue-50/30 transition-all rounded-lg shadow-sm px-4 py-0 text-sm font-semibold"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
            <tr>
              <th className="px-4 py-3 text-left w-12 bg-gray-50">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-brand focus:ring-brand h-4 w-4"
                  checked={regularizationRequests.length > 0 && selectedRows.size === regularizationRequests.length}
                  onChange={handleSelectAll}
                />
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-blue-100/60 select-none bg-gray-50"
                onClick={() => handleSort('employee')}
              >
                <div className="flex items-center space-x-1">
                  <span>Employee</span>
                  {sortField === 'employee' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-brand" /> :
                      <ArrowDown className="h-3 w-3 text-brand" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-blue-100/60 select-none bg-gray-50"
                onClick={() => handleSort('regularizationDate')}
              >
                <div className="flex items-center space-x-1">
                  <span>Regularization Date</span>
                  {sortField === 'regularizationDate' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-brand" /> :
                      <ArrowDown className="h-3 w-3 text-brand" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[150px] cursor-pointer hover:bg-blue-100/60 select-none bg-gray-50"
                onClick={() => handleSort('requestedTime')}
              >
                <div className="flex items-center space-x-1">
                  <span>Requested Time</span>
                  {sortField === 'requestedTime' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-brand" /> :
                      <ArrowDown className="h-3 w-3 text-brand" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[180px] cursor-pointer hover:bg-blue-100/60 select-none bg-gray-50"
                onClick={() => handleSort('reason')}
              >
                <div className="flex items-center space-x-1">
                  <span>Reason</span>
                  {sortField === 'reason' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-brand" /> :
                      <ArrowDown className="h-3 w-3 text-brand" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-blue-100/60 select-none bg-gray-50"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center space-x-1">
                  <span>Status</span>
                  {sortField === 'status' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-brand" /> :
                      <ArrowDown className="h-3 w-3 text-brand" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[200px] cursor-pointer hover:bg-blue-100/60 select-none bg-gray-50"
                onClick={() => handleSort('requestDate')}
              >
                <div className="flex items-center space-x-1">
                  <span>Request Date</span>
                  {sortField === 'requestDate' ? (
                    sortOrder === 'asc' ?
                      <ArrowUp className="h-3 w-3 text-brand" /> :
                      <ArrowDown className="h-3 w-3 text-brand" />
                  ) : (
                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider min-w-[130px] bg-gray-50">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading || isSearching ? (
              <tr>
                <td colSpan="8" className="px-6 py-8 text-center">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand"></div>
                    <span className="text-gray-500">
                      {isSearching ? "Searching..." : "Loading regularization requests..."}
                    </span>
                  </div>
                </td>
              </tr>
            ) : regularizationRequests.length > 0 ? (
              getSortedRequests(regularizationRequests).map((request) => (
                <tr
                  key={request._id || request.id}
                  className={`hover:bg-blue-50/30 ${selectedRows.has(request._id || request.id) ? 'bg-brand/5' : ''}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-brand focus:ring-brand h-4 w-4"
                      checked={selectedRows.has(request._id || request.id)}
                      onChange={() => handleSelectRow(request._id || request.id)}
                    />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap overflow-hidden">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate capitalize">
                        {request.employeeName ||
                          (request.employeeId?.personalInfo
                            ? `${request.employeeId.personalInfo.firstName || ""
                              } ${request.employeeId.personalInfo.lastName || ""
                              }`.trim()
                            : "Unknown Employee")}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {request.employeeId?.employeeId || "No ID"}
                      </div>
                      <div className="text-xs text-gray-400 truncate">
                        {request.employeeId?.user?.email || "Unknown Email"}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(request.date, { includeTime: false })}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="min-w-0">
                      <div>In: {formatTime(request.requestedCheckIn)}</div>
                      <div>Out: {formatTime(request.requestedCheckOut)}</div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="truncate max-w-[170px] capitalize" title={request.reason}>
                      {request.reason}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(request.status)}
                      <span
                        className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(
                          request.status
                        )}`}
                      >
                        {request.status.charAt(0).toUpperCase() +
                          request.status.slice(1)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(request.createdAt || request.submittedDate, { includeTime: true })}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(request)}
                        className="text-brand hover:text-brand-dark"
                        aria-label="View regularisation request details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {request.status === "pending" && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => triggerStatusChange(request, "approved")}
                            className="text-green-600 hover:text-green-800 hover:bg-green-50 px-2 py-1"
                            title="Approve this request"
                            aria-label="Approve regularisation request"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => triggerStatusChange(request, "rejected")}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 px-2 py-1"
                            title="Reject this request"
                            aria-label="Reject regularisation request"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <PermissionGate permission="attendance" action="delete">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRequest(request)}
                          className="text-red-500 hover:text-red-800"
                          aria-label="Delete regularization request"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </PermissionGate>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="bg-white hover:bg-white">
                <td colSpan="8" className="px-6 py-12 text-center text-gray-500 italic">
                  No regularization requests found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {/* Pagination - integrated into the table card */}
        {
          (() => {
            const regularizationTotalItems = Number(pagination?.totalItems || regularizationRequests.length || 0);
            const regularizationTotalPages = Number(pagination?.totalPages || 1);
            const effectivePaginationLimit = pagination?.limit || pagination?.itemsPerPage || itemsPerPage || 10;
            const currentPageNum = pagination?.currentPage || currentPage || 1;
            const shouldShowPagination = !isLoading && !isSearching;
            const handleRegularizationPageChange = (page) => {
              setCurrentPage(page);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            };

            // Calculate hasPrevPage and hasNextPage if not provided
            const hasPrevPage = pagination?.hasPrevPage !== undefined
              ? pagination.hasPrevPage
              : currentPageNum > 1;
            const hasNextPage = pagination?.hasNextPage !== undefined
              ? pagination.hasNextPage
              : currentPageNum < regularizationTotalPages;

            return shouldShowPagination ? (
                <PaginationControls
                  currentPage={currentPageNum}
                  totalPages={regularizationTotalPages}
                  totalItems={regularizationTotalItems}
                  limit={effectivePaginationLimit}
                  hasNextPage={hasNextPage}
                  hasPrevPage={hasPrevPage}
                  onPageChange={handleRegularizationPageChange}
                  onLimitChange={handleItemsPerPageChange}
                  limitOptions={[10, 25, 50, 100]}
                  border={false}
                />
            ) : null;
          })()
        }
      </div>

      {
        !isLoading && !isSearching && regularizationRequests.length === 0 && (
          <div className="text-center py-12">
            <Edit className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchMetadata && searchMetadata.appliedFilters > 0
                ? "No regularization requests found matching your search criteria"
                : "No regularization requests found"
              }
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchMetadata && searchMetadata.appliedFilters > 0
                ? "Try adjusting your search criteria or clear filters to see all requests."
                : "Create a new regularization request to get started."
              }
            </p>
          </div>
        )
      }


      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Regularization Requests"
        showDateRange
        scopedFormatExport={{
          onExport: handleScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Confirmation Modal */}
      {
        confirmModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] !mt-0 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
              <div className={`p-1 h-2 ${confirmModal.status === 'approved' ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className={`p-2 rounded-full mr-3 ${confirmModal.status === 'approved' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                    {confirmModal.status === 'approved' ? <CheckCircle className="h-6 w-6" /> : <AlertCircle className="h-6 w-6" />}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {confirmModal.status === 'approved' ? 'Approve' : 'Reject'} Request
                  </h3>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600">
                    Are you sure you want to <strong>{confirmModal.status}</strong> the regularization request for <strong>{confirmModal.employeeName}</strong>?
                  </p>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Comments {confirmModal.status === 'rejected' ? '*' : '(Optional)'}
                    </label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand outline-none resize-none h-24"
                      placeholder={`Enter ${confirmModal.status === 'rejected' ? 'rejection reason' : 'comments'}...`}
                      value={confirmModal.comments}
                      maxLength={1000}
                      onChange={(e) => setConfirmModal({ ...confirmModal, comments: e.target.value })}
                    />
                    <div className="flex justify-end mt-1">
                      <span className={`text-xs ${confirmModal.comments.length >= 900 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                        {confirmModal.comments.length}/1000
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmAction}
                    className={confirmModal.status === 'approved' ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
                    disabled={confirmModal.status === 'rejected' && !confirmModal.comments.trim()}
                  >
                    Confirm {confirmModal.status === 'approved' ? 'Approval' : 'Rejection'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {/* Delete Confirmation Modal */}
      <WarningPopup
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setRequestToDelete(null);
          setIsBulkDelete(false);
        }}
        onConfirm={confirmDeleteRequest}
        title={isBulkDelete ? "Delete Regularization Requests" : "Delete Regularization Request"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedRows.size} selected regularization requests? This action cannot be undone.`
            : "Are you sure you want to delete this regularization request? This action cannot be undone."
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* New Regularization Request Modal */}
      <RegularizationRequestModal
        isOpen={showRequestForm}
        onClose={() => {
          setShowRequestForm(false);
          setSelectedEmployeeData(null);
          setEmployeeSearchOpen(false);
          setEmployeeSearchTerm("");
          if (onClose) onClose();
        }}
        onSuccess={() => {
          loadRegularizationRequests();
        }}
      />
    </div >
  );
};

export default AttendanceRegularization;
